import { PromoBanner as IPromoBannerBlock } from 'app/blocks/promo-banner'
import { RenderBlocks } from 'app/components/blocks/RenderBlocks'
import { FullScreenOverlay } from 'app/components/primitive/FullScreenOverlay'
import { Meta } from 'app/components/primitive/Meta'
import Spinner from 'app/components/primitive/Spinner'
import MainLayout from 'app/components/structure/Layout/MainLayout'
import NewsletterSignup from 'app/components/structure/NewsletterSignup'
import useQuery from 'app/hooks/use-query'
import {
  createLandingQuery,
  mapToLandingContent,
} from 'app/queries/globals/landing'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef } from 'react'

const landingQuery = createLandingQuery()

const getVideoExpiryFromData = (videoData?: string): number | null => {
  if (!videoData) return null

  try {
    const parsed = JSON.parse(videoData)
    const playlistItem = parsed?.playlist?.[0]
    const fileUrl =
      playlistItem?.file ||
      playlistItem?.sources?.[0]?.file ||
      playlistItem?.sources?.[0]?.src

    if (!fileUrl || typeof fileUrl !== 'string') return null

    const match = fileUrl.match(/[?&]exp=(\d+)/)
    if (!match?.[1]) return null

    const exp = Number(match[1])
    return Number.isFinite(exp) ? exp : null
  } catch (error) {
    return null
  }
}

const isVideoDataExpired = (videoData?: string, skewSeconds = 30) => {
  const exp = getVideoExpiryFromData(videoData)
  if (!exp) return false

  const now = Math.floor(Date.now() / 1000)
  return exp <= now + skewSeconds
}

export const LandingScreen = observer(() => {
  const {
    data: pageData,
    loading,
    fetchData,
    refetching,
  } = useQuery(landingQuery, {}, mapToLandingContent, {
    // Video URLs in videoData are signed by JWPlayer with their own expiration.
    // Force refetch to ensure fresh URLs, preventing 403 errors on manifest loads.
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const pageContent = pageData?.pageContent

  const heroVideoData = useMemo(() => {
    const heroBlock = pageContent?.find(
      (block) => block.__typename === 'LandingHero',
    ) as any

    return heroBlock?.heroVideo?.video?.[0]?.videoData as string | undefined
  }, [pageContent])

  const heroVideoExpired = useMemo(
    () => isVideoDataExpired(heroVideoData),
    [heroVideoData],
  )

  const hasTriggeredRefetch = useRef(false)

  useEffect(() => {
    if (!heroVideoExpired) {
      hasTriggeredRefetch.current = false
      return
    }

    if (refetching || hasTriggeredRefetch.current) return

    hasTriggeredRefetch.current = true
    fetchData()
  }, [heroVideoExpired, fetchData, refetching])

  const promoBannerBlock = pageContent?.find(
    (block) => block.__typename === 'PromoBanner',
  ) as IPromoBannerBlock

  const isLoading = loading && !pageData

  return (
    <MainLayout
      noSidebar
      noFooter={isLoading}
      noSearch
      promoBannerProps={promoBannerBlock}
      contentStyles={{
        gap: '$13',
        $gtMd: {
          gap: '$20',
        },
      }}
    >
      <Meta title="Welcome" />

      <RenderBlocks
        blocks={pageContent || []}
        gap="$13"
        $gtSm={{ gap: '$20' }}
        landingHeroProps={{
          videoRefreshing: heroVideoExpired || refetching,
        }}
      />

      {!isLoading && <NewsletterSignup />}

      {isLoading && (
        <FullScreenOverlay bg="$bgPrimary">
          <Spinner size="large" />
        </FullScreenOverlay>
      )}
    </MainLayout>
  )
})
